/**
 * Value Binding Editor - picks where an output node's value comes from (a literal, a
 * workflow input, an upstream node's output field, or a field of the source item).
 *
 * Mirrors OutputValueBinding (raiqon/schemas/workflow.py): each source shows only the
 * fields its backend validator (validate_source_consistency) allows for that source.
 * Shared by PushbackConfig and CreateItemConfig's field-mapping rows.
 */

import React from 'react';
import { isDataSourceNode } from '../api/client';
import type { PipelineDefinition, PipelineNode } from '../types/pipeline';
import { OutputPathPicker, useAgentOutputSchema } from './OutputPathPicker';

export const VALUE_SOURCE_OPTIONS = ['literal', 'workflow_input', 'step_output', 'item_field'] as const;

export type OutputValueSource = (typeof VALUE_SOURCE_OPTIONS)[number];

export interface OutputValueBindingConfig {
  source: OutputValueSource;
  literal_value?: string;
  source_name?: string;
  upstream_step_reference?: string;
  output_field?: string;
  item_field?: string;
}

const SOURCE_LABELS: Record<OutputValueSource, string> = {
  literal: 'Literal value',
  workflow_input: 'Workflow input',
  step_output: 'Upstream node output',
  item_field: 'Source item field',
};

export function isBindingComplete(binding: OutputValueBindingConfig): boolean {
  switch (binding.source) {
    case 'literal':
      return (binding.literal_value ?? '').trim() !== '';
    case 'workflow_input':
      return (binding.source_name ?? '').trim() !== '';
    case 'step_output':
      return (binding.upstream_step_reference ?? '').trim() !== '' && (binding.output_field ?? '').trim() !== '';
    case 'item_field':
      return (binding.item_field ?? '').trim() !== '';
  }
}

// Strip fields that don't belong to the selected source - the backend binding model
// is strict (extra='forbid') and rejects any field set for a different source.
export function normalizeBinding(binding: OutputValueBindingConfig): OutputValueBindingConfig {
  switch (binding.source) {
    case 'literal':
      return { source: 'literal', literal_value: binding.literal_value ?? '' };
    case 'workflow_input':
      return { source: 'workflow_input', source_name: binding.source_name };
    case 'step_output':
      return {
        source: 'step_output',
        upstream_step_reference: binding.upstream_step_reference,
        output_field: binding.output_field,
      };
    case 'item_field':
      return { source: 'item_field', item_field: binding.item_field };
  }
}

/** Nodes with an edge into `nodeId` - the only nodes whose output a binding may read. */
export function getUpstreamNodes(pipeline: PipelineDefinition | null, nodeId: string): PipelineNode[] {
  if (!pipeline) return [];
  const sourceIds = new Set(pipeline.edges.filter((e) => e.target === nodeId).map((e) => e.source));
  return pipeline.nodes.filter((n) => sourceIds.has(n.id) && !isDataSourceNode(n));
}

interface ValueBindingEditorProps {
  idPrefix: string;
  binding: OutputValueBindingConfig;
  onChange: (binding: OutputValueBindingConfig) => void;
  pipeline: PipelineDefinition | null;
  nodeId: string;
  availableWorkflowInputs: string[];
  itemFields: string[];
  // item_field is meaningless for a step with no single source item (aggregate).
  allowItemField?: boolean;
}

export const ValueBindingEditor: React.FC<ValueBindingEditorProps> = ({
  idPrefix,
  binding,
  onChange,
  pipeline,
  nodeId,
  availableWorkflowInputs,
  itemFields,
  allowItemField = true,
}) => {
  const upstreamNodes = getUpstreamNodes(pipeline, nodeId);
  const selectedUpstream = pipeline?.nodes.find((n) => n.id === binding.upstream_step_reference);
  const { schema: upstreamSchema, loading: upstreamSchemaLoading } = useAgentOutputSchema(selectedUpstream?.agent_id);
  const sources = VALUE_SOURCE_OPTIONS.filter((s) => allowItemField || s !== 'item_field');

  // Keep an already-saved upstream reference selectable even if its edge was removed,
  // so the user sees what is configured instead of a silently blank select.
  const upstreamOptions =
    selectedUpstream && !upstreamNodes.some((n) => n.id === selectedUpstream.id)
      ? [...upstreamNodes, selectedUpstream]
      : upstreamNodes;

  return (
    <div className="value-binding-editor">
      <select
        className="condition-input"
        value={binding.source}
        onChange={(e) => onChange({ ...binding, source: e.target.value as OutputValueSource })}
      >
        {sources.map((s) => (
          <option key={s} value={s}>
            {SOURCE_LABELS[s]}
          </option>
        ))}
      </select>

      {binding.source === 'literal' && (
        <input
          type="text"
          className="condition-input"
          value={binding.literal_value ?? ''}
          onChange={(e) => onChange({ ...binding, literal_value: e.target.value })}
          placeholder="Value"
        />
      )}

      {binding.source === 'workflow_input' &&
        (availableWorkflowInputs.length > 0 ? (
          <select
            className="condition-input"
            value={binding.source_name ?? ''}
            onChange={(e) => onChange({ ...binding, source_name: e.target.value })}
          >
            <option value="">Select an input...</option>
            {availableWorkflowInputs.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            className="condition-input"
            value={binding.source_name ?? ''}
            onChange={(e) => onChange({ ...binding, source_name: e.target.value })}
            placeholder="Workflow input name"
          />
        ))}

      {binding.source === 'step_output' && (
        <>
          <select
            className="condition-input"
            value={binding.upstream_step_reference ?? ''}
            onChange={(e) => onChange({ ...binding, upstream_step_reference: e.target.value, output_field: '' })}
          >
            <option value="">{upstreamOptions.length ? 'Select an upstream node...' : 'Connect an upstream node first'}</option>
            {upstreamOptions.map((n) => (
              <option key={n.id} value={n.id}>
                {n.label || n.id}
              </option>
            ))}
          </select>
          {binding.upstream_step_reference && (
            <OutputPathPicker
              value={binding.output_field ?? ''}
              onChange={(output_field) => onChange({ ...binding, output_field })}
              schema={upstreamSchema}
              loading={upstreamSchemaLoading}
            />
          )}
        </>
      )}

      {binding.source === 'item_field' && (
        <>
          <input
            type="text"
            className="condition-input"
            list={`${idPrefix}-item-fields`}
            value={binding.item_field ?? ''}
            onChange={(e) => onChange({ ...binding, item_field: e.target.value })}
            placeholder="Item field, e.g. rq_summary"
          />
          <datalist id={`${idPrefix}-item-fields`}>
            {itemFields.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
        </>
      )}
    </div>
  );
};
